import React from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "src/components/base/dialog";
import { Button, PrimaryButton } from "src/components/base/button";
import { ErrorMessage, Field, FieldGroup, Fieldset, Label, RequiredLabel } from "src/components/base/fieldset";
import { Combobox, ComboboxOption } from "src/components/base/combobox";
import { toast } from "react-toastify";
import ACCESS_RULE_CONTEXT from "src/context/internal/access-rule";
import { ApiInternal } from "src/api/api_internal";
import { SimpleGroup, SimpleUser } from "src/api/generated/frontend_internal_v1";

/**
 * The properties for {@link AddToGroupDialog}
 */
export type AddToGroupDialogProps = {
    /** Callback for closing the dialog */
    onClose: () => void;
    /** The user to add to the group */
    user: SimpleUser;
};

/**
 * A dialog for adding a user to a group
 */
export default function AddToGroupDialog(props: AddToGroupDialogProps) {
    const [t] = useTranslation("dialog-add-to-group");
    const [tg] = useTranslation();

    const options = React.useContext(ACCESS_RULE_CONTEXT);

    const [value, setValue] = React.useState<null | SimpleGroup>(null);
    const [errors, setErrors] = React.useState<string[]>([]);
    const [query, setQuery] = React.useState("");

    const groups = options.options.groups
        .filter((group) => group.provider === "Local")
        .filter((group) => group.name.toLowerCase().includes(query.toLowerCase()));

    /**
     * Helper function to add a user to a group
     */
    const addToGroup = async () => {
        if (value === null) {
            setErrors(() => [t("error.field-must-be-filled")]);
            return;
        }

        const _res = await ApiInternal.groups.local.addMember(value.uuid, props.user.uuid);

        toast.success(t("toast.user-added-successfully-to-group"));
        props.onClose();
    };

    return (
        <Dialog open={true} onClose={props.onClose}>
            <DialogTitle>{t("heading.add-user-to-group", { user: props.user.display_name })}</DialogTitle>

            <DialogBody>
                <Fieldset>
                    <FieldGroup>
                        <Field>
                            <RequiredLabel>{t("label.select-group")}</RequiredLabel>
                            <Combobox
                                value={value}
                                onChange={(x) => setValue(x)}
                                onClose={() => setQuery("")}
                                queryDisplay={(q) => (q ? `group:${q.name}` : "")}
                                onQueryChange={(q) => setQuery(q)}
                                invalid={errors.length > 0}
                            >
                                {groups.map((group) => (
                                    <ComboboxOption key={group.uuid} value={group}>
                                        <Label>{`group:${group.name}`}</Label>
                                    </ComboboxOption>
                                ))}
                            </Combobox>
                            {errors.map((err) => (
                                <ErrorMessage>{err}</ErrorMessage>
                            ))}
                        </Field>
                    </FieldGroup>
                </Fieldset>
            </DialogBody>

            <DialogActions>
                <Button plain={true}>{tg("button.cancel")}</Button>
                <PrimaryButton onClick={() => addToGroup().then()}>{t("button.add-to-group")}</PrimaryButton>
            </DialogActions>
        </Dialog>
    );
}
